import { useMemo, useState } from 'react';
import { selectVisible, toggleSelection } from '../../lib/portalAssets/bulk';
import type { PortalAssetTile } from '../../types/portalAsset';

// The selection behind the staff grids' bulk bar. STAFF ONLY (never imported by the client portal).
//
// ⚠️ THE SELECTION IS ONLY EVER WHAT IS VISIBLE. `selectedTiles` is the loaded tiles whose id is selected,
// and that — not the stored id set — is what a bulk action sends. A filter change, a deleted asset or a
// reload can leave an id behind in the set; acting on something the person can no longer see would be the
// worst possible bulk bug, so ids that are not on screen are simply not part of the selection.
// ⚠️ The cap (BULK_MAX_ASSETS) is enforced here as the person selects, with a sentence saying so, rather than
// discovered as a 400 after pressing the button.

export function useBulkSelection(tiles: readonly PortalAssetTile[]) {
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set());
  const [note, setNote] = useState<string | null>(null);

  const selectedTiles = useMemo(() => tiles.filter((t) => ids.has(t.id)), [tiles, ids]);
  const visibleSelected = useMemo(() => new Set(selectedTiles.map((t) => t.id)), [selectedTiles]);

  return {
    selectedTiles,
    isSelected: (id: string) => visibleSelected.has(id),
    toggle: (id: string) => {
      const { next, atCap } = toggleSelection(visibleSelected, id);
      setIds(next);
      setNote(atCap ? 'You can select up to 50 at a time.' : null);
    },
    selectAllVisible: () => {
      const { next, leftOut } = selectVisible(
        tiles.map((t) => t.id),
        visibleSelected,
      );
      setIds(next);
      setNote(leftOut > 0 ? `Selected 50. ${leftOut} more on this page were left out — you can select up to 50 at a time.` : null);
    },
    clear: () => {
      setIds(new Set());
      setNote(null);
    },
    /** After a run: keep ONLY the ones that did not go through selected, so they can be looked at or retried. */
    keepOnly: (keep: readonly string[]) => {
      setIds(new Set(keep));
      setNote(null);
    },
    note,
  };
}
